/**
 * RELEASE COMPARISON CHART
 *
 * Grouped bar chart side-by-siding each release's totalTestCases, executed
 * count, and not-executed count. Useful for "which release has the most
 * coverage gaps?" at a glance. A secondary line overlays pass rate.
 *
 * Props:
 *   - releases: Array<ReleaseExecutionSummary>  (same shape as /releases endpoint)
 *   - height: number (default 300)
 */

import React, { useMemo } from 'react'
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts'
import { token } from '@atlaskit/tokens'

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null
  return (
    <div
      style={{
        backgroundColor: token('elevation.surface.overlay', '#FFFFFF'),
        border: `1px solid ${token('color.border', '#DFE1E6')}`,
        borderRadius: '3px',
        padding: '8px 10px',
        fontSize: '12px',
        boxShadow: '0 2px 6px rgba(9, 30, 66, 0.25)'
      }}
    >
      <div style={{ fontWeight: 600, color: token('color.text', '#172B4D'), marginBottom: '4px' }}>
        {label}
      </div>
      {payload.map((entry) => (
        <div
          key={entry.dataKey}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}
        >
          <span
            style={{
              width: '10px',
              height: '10px',
              borderRadius: '2px',
              backgroundColor: entry.color
            }}
          />
          <span style={{ color: token('color.text.subtle', '#6B778C') }}>{entry.name}:</span>
          <strong style={{ color: token('color.text', '#172B4D') }}>
            {entry.dataKey === 'passRate' ? `${Number(entry.value).toFixed(1)}%` : entry.value}
          </strong>
        </div>
      ))}
    </div>
  )
}

const ReleaseComparisonChart = ({ releases = [], height = 300 }) => {
  // Flatten each release into the row shape Recharts wants.
  const data = useMemo(
    () =>
      (releases || []).map((r) => ({
        releaseName: r.releaseName,
        totalTestCases: r.summary?.totalTestCases || 0,
        executedTestCases: r.summary?.executedTestCases || 0,
        notExecutedTestCases: r.summary?.notExecutedTestCases || 0,
        passRate: r.summary?.passRate || 0
      })),
    [releases]
  )

  if (!data.length) {
    return (
      <div
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: token('color.text.subtle', '#6B778C'),
          fontSize: '13px'
        }}
      >
        No releases to compare yet.
      </div>
    )
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 16, right: 48, left: 0, bottom: 8 }}>
        <CartesianGrid stroke={token('color.border', '#DFE1E6')} strokeDasharray="3 3" />
        <XAxis
          dataKey="releaseName"
          tick={{ fontSize: 11, fill: token('color.text.subtle', '#6B778C') }}
          stroke={token('color.border', '#DFE1E6')}
        />
        <YAxis
          yAxisId="count"
          tick={{ fontSize: 11, fill: token('color.text.subtle', '#6B778C') }}
          stroke={token('color.border', '#DFE1E6')}
          allowDecimals={false}
        />
        <YAxis
          yAxisId="pct"
          orientation="right"
          domain={[0, 100]}
          tickFormatter={(v) => `${v}%`}
          tick={{ fontSize: 11, fill: token('color.text.subtle', '#6B778C') }}
          stroke={token('color.border', '#DFE1E6')}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: token('color.background.neutral', '#F4F5F7') }} />
        <Legend wrapperStyle={{ fontSize: '12px' }} />
        <Bar
          yAxisId="count"
          dataKey="executedTestCases"
          name="Executed"
          fill={token('color.background.success.bold', '#22A06B')}
          radius={[2, 2, 0, 0]}
          isAnimationActive={false}
        />
        <Bar
          yAxisId="count"
          dataKey="notExecutedTestCases"
          name="Not executed"
          fill={token('color.background.neutral', '#DFE1E6')}
          radius={[2, 2, 0, 0]}
          isAnimationActive={false}
        />
        <Line
          yAxisId="pct"
          type="monotone"
          dataKey="passRate"
          name="Pass rate"
          stroke={token('color.background.information.bold', '#0065FF')}
          strokeWidth={2}
          dot={{ r: 3 }}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  )
}

export default ReleaseComparisonChart
